import { useState, useEffect, useCallback } from 'react';
import { getStaffLedger } from '../lib/api';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { FileXls, CaretDown, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function StaffLedgerTab() {
  const [data, setData] = useState({ rows: [], grand_totals: {} });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await getStaffLedger(params);
      setData(data || { rows: [], grand_totals: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Staff Ledger');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get('/staff-ledger/audit/export/excel', {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      a.download = match ? match[1] : 'staff_ledger.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const totals = data.grand_totals || {};

  return (
    <div className="space-y-6" data-testid="staff-ledger-tab">
      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="p-4 border border-blue-200 bg-blue-50 rounded-md" data-testid="sl-summary-deposits">
          <div className="text-xs uppercase text-blue-700 font-medium">Total Deposits (In)</div>
          <div className="text-lg font-bold mt-1 text-blue-900">{fmtINR(totals.total_deposits)}</div>
        </div>
        <div className="p-4 border border-red-200 bg-red-50 rounded-md" data-testid="sl-summary-spend">
          <div className="text-xs uppercase text-red-700 font-medium">Total Spend (Out)</div>
          <div className="text-lg font-bold mt-1 text-red-900">{fmtINR(totals.total_spend)}</div>
        </div>
        <div className="p-4 border border-emerald-200 bg-emerald-50 rounded-md" data-testid="sl-summary-refunds">
          <div className="text-xs uppercase text-emerald-700 font-medium">Refunds (Back)</div>
          <div className="text-lg font-bold mt-1 text-emerald-900">{fmtINR(totals.total_refunds)}</div>
        </div>
        <div className={`p-4 border rounded-md ${(totals.net_balance || 0) >= 0 ? 'border-gray-200 bg-gray-50' : 'border-red-300 bg-red-100'}`} data-testid="sl-summary-net">
          <div className="text-xs uppercase text-gray-700 font-medium">Net Balance</div>
          <div className={`text-lg font-bold mt-1 ${(totals.net_balance || 0) >= 0 ? 'text-gray-900' : 'text-red-800'}`}>{fmtINR(totals.net_balance)}</div>
        </div>
        <div className="p-4 border border-gray-300 bg-gray-900 text-white rounded-md">
          <div className="text-xs uppercase opacity-80 font-medium">Staff Count</div>
          <div className="text-lg font-bold mt-1">{totals.staff_count || 0}</div>
          <div className="text-[11px] opacity-70">{totals.booking_count || 0} bookings</div>
        </div>
      </div>

      {/* Filters + Export */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="sl-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" data-testid="sl-date-to" />
        </div>
        <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }} className="h-9 text-xs">Reset</Button>
        <div className="ml-auto">
          <Button variant="outline" className="h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={downloadExcel} data-testid="sl-export-excel">
            <FileXls size={14} className="mr-1" /> Excel Export
          </Button>
        </div>
      </div>

      {/* Main Table */}
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-8"></TableHead>
              <TableHead>Staff</TableHead>
              <TableHead className="text-right">Deposits (In)</TableHead>
              <TableHead className="text-right">Spend (Out)</TableHead>
              <TableHead className="text-right">Refunds</TableHead>
              <TableHead className="text-right">Net Balance</TableHead>
              <TableHead className="text-center">Bookings</TableHead>
              <TableHead className="text-center">Agencies</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-6">Loading…</TableCell></TableRow>
            ) : data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-6">No staff activity</TableCell></TableRow>
            ) : data.rows.map(r => (
              <>
                <TableRow key={r.staff_id} data-testid={`sl-row-${r.staff_id}`} className={r.net_balance < 0 ? 'bg-red-50' : ''}>
                  <TableCell>
                    {(r.agency_breakdown.length > 0 || (r.sub_staff && r.sub_staff.length > 0)) && (
                      <button onClick={() => toggleExpand(r.staff_id)} className="text-gray-500 hover:text-gray-900" data-testid={`sl-expand-${r.staff_id}`}>
                        {expanded.has(r.staff_id) ? <CaretDown size={16} /> : <CaretRight size={16} />}
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    <div>{r.staff_name}</div>
                    <div className="text-[11px] text-gray-500 font-normal">
                      Staff · {r.email}
                      {r.sub_staff_count > 0 && <span className="ml-1 text-blue-700">· {r.sub_staff_count} sub-staff</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-blue-700 font-semibold">{fmtINR(r.total_deposits)}<div className="text-[10px] text-gray-500 font-normal">{r.deposit_count} entries</div></TableCell>
                  <TableCell className="text-right text-red-700 font-semibold">{fmtINR(r.total_spend)}<div className="text-[10px] text-gray-500 font-normal">{r.booking_count} bookings</div></TableCell>
                  <TableCell className="text-right text-emerald-700">{fmtINR(r.total_refunds)}<div className="text-[10px] text-gray-500 font-normal">{r.refund_count} entries</div></TableCell>
                  <TableCell className={`text-right font-bold ${r.net_balance < 0 ? 'text-red-800' : 'text-gray-900'}`}>
                    {fmtINR(r.net_balance)}
                    {r.net_balance < 0 && <div className="text-[10px] text-red-600 font-medium">⚠️ Staff owes</div>}
                  </TableCell>
                  <TableCell className="text-center">{r.booking_count}</TableCell>
                  <TableCell className="text-center">{r.agency_breakdown.length}</TableCell>
                </TableRow>
                {expanded.has(r.staff_id) && (r.agency_breakdown.length > 0 || (r.sub_staff && r.sub_staff.length > 0)) && (
                  <TableRow className="bg-gray-50">
                    <TableCell></TableCell>
                    <TableCell colSpan={7}>
                      {r.sub_staff && r.sub_staff.length > 0 && (
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-gray-600 mb-1 uppercase">Sub-Staff under {r.staff_name} ({r.sub_staff.length})</div>
                          <Table className="border">
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead className="text-xs">Sub-Staff</TableHead>
                                <TableHead className="text-xs text-right">Deposits</TableHead>
                                <TableHead className="text-xs text-right">Spend</TableHead>
                                <TableHead className="text-xs text-right">Refunds</TableHead>
                                <TableHead className="text-xs text-right">Net</TableHead>
                                <TableHead className="text-xs text-right">Bookings</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {r.sub_staff.map(ss => (
                                <TableRow key={ss.staff_id} data-testid={`sl-sub-${ss.staff_id}`} className={ss.net_balance < 0 ? 'bg-red-50' : ''}>
                                  <TableCell className="text-sm">
                                    <div>{ss.staff_name}</div>
                                    <div className="text-[10px] text-gray-500">{ss.email}</div>
                                  </TableCell>
                                  <TableCell className="text-right text-sm text-blue-700">{fmtINR(ss.total_deposits)}</TableCell>
                                  <TableCell className="text-right text-sm text-red-700">{fmtINR(ss.total_spend)}</TableCell>
                                  <TableCell className="text-right text-sm text-emerald-700">{fmtINR(ss.total_refunds)}</TableCell>
                                  <TableCell className={`text-right text-sm font-semibold ${ss.net_balance < 0 ? 'text-red-800' : 'text-gray-900'}`}>{fmtINR(ss.net_balance)}</TableCell>
                                  <TableCell className="text-right text-sm">{ss.booking_count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                      {r.agency_breakdown.length > 0 && (
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-1 uppercase">Agency-wise spend (Staff + Sub-Staff combined)</div>
                          <Table className="border">
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead className="text-xs">Agency</TableHead>
                                <TableHead className="text-xs text-right">Spend</TableHead>
                                <TableHead className="text-xs text-right">Qty</TableHead>
                                <TableHead className="text-xs text-right">Bookings</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {r.agency_breakdown.map((b, idx) => (
                                <TableRow key={b.agency_id || idx} data-testid={`sl-agency-${r.staff_id}-${b.agency_id}`}>
                                  <TableCell className="text-sm">{b.agency_name}</TableCell>
                                  <TableCell className="text-right text-sm font-medium">{fmtINR(b.spend)}</TableCell>
                                  <TableCell className="text-right text-sm">{b.qty}</TableCell>
                                  <TableCell className="text-right text-sm">{b.booking_count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
